<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOnboarding } from './useOnboarding';

const { t } = useI18n();
const { getChecklist, getChecklistState, getCompletedCount, getTotalCount } =
  useOnboarding();

const checklistItems = computed(() => {
  return getChecklist.value.map(key => ({
    key,
    label: t(`ONBOARDING_TOUR.CHECKLIST.${key.toUpperCase()}`),
    completed: !!getChecklistState.value[key],
  }));
});

const progressPercent = computed(() => {
  if (getTotalCount.value === 0) return 0;
  return Math.round((getCompletedCount.value / getTotalCount.value) * 100);
});

const isComplete = computed(() => progressPercent.value === 100);
</script>

<template>
  <div
    class="bg-n-surface-2 border border-n-weak rounded-lg p-4 flex flex-col gap-3"
  >
    <div class="flex items-center justify-between">
      <p class="text-sm font-semibold text-n-slate-12">
        {{ t('ONBOARDING_TOUR.CHECKLIST.TITLE') }}
      </p>
      <span class="text-xs text-n-slate-11">
        {{ getCompletedCount }}/{{ getTotalCount }}
      </span>
    </div>

    <div class="h-1.5 bg-n-weak rounded-full overflow-hidden">
      <div
        class="h-full bg-n-brand rounded-full transition-all duration-300"
        :style="{ width: `${progressPercent}%` }"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <div
        v-for="item in checklistItems"
        :key="item.key"
        class="flex items-center gap-2 text-sm"
      >
        <span
          class="size-4 rounded-full flex items-center justify-center text-xs flex-shrink-0"
          :class="
            item.completed
              ? 'bg-n-brand text-white'
              : 'border border-n-weak text-n-slate-10'
          "
        >
          <span v-if="item.completed">{{ '\u2713' }}</span>
          <span v-else class="size-1.5 rounded-full bg-n-slate-10" />
        </span>
        <span
          :class="
            item.completed ? 'text-n-slate-10 line-through' : 'text-n-slate-12'
          "
        >
          {{ item.label }}
        </span>
      </div>
    </div>

    <p
      v-if="isComplete"
      class="text-xs text-n-brand font-medium text-center mt-1"
    >
      {{ t('ONBOARDING_TOUR.CHECKLIST.COMPLETED') }}
    </p>
  </div>
</template>
